import React, { useEffect, useState } from 'react';
import styles from '../profile-view/profile-view.module.scss';
import { Avatar, BottomSheet, Button, Container, Spiner, Tag, Typography } from '@/ui-kit';
import { useParams } from 'react-router-dom';
import { useGetMyProfile } from '@/hooks/use-get-my-profile';
import { GalleryImages } from '@/components/gallery-images';
import { differenceInYearsResult } from '@/utils/difference-in-years-result';
import clsx from 'clsx';
import { ArrowTopFillIcon, LocationIcon, MagicIcon, PremiumIcon, VerifiedAccountIcon } from '@/assets/icons';
import { OnlineStatus, OfflineStatus } from '@/assets/icons';
import { newDataPurposes } from '@/constans/purposes';
import { useDrag } from '@use-gesture/react';
import { useAuth } from '@/store/useAuth';
import { ProfileViewModel } from '@/types/vm/profile';
import { getProfileNameAndAge } from '@/utils/msgs';
import { useTranslation } from 'react-i18next';
import { AdditionalInfo } from '@/components/additional-info';

interface Props {
    id?: number;
}

export const MyFormPreview: React.FC<Props> = (props) => {
    const id = props.id?.toString() ?? useParams<{ id: string }>().id;
    const { data: profileData, isError, isLoading } = useGetMyProfile();
    const [isVisibleDetails, setIsVisibleDetails] = useState(false);

    const currentPurpose = newDataPurposes.find((purpose) => purpose.value === profileData?.data?.purpose);

    const getIconComponent = (icon: React.FC<React.SVGProps<SVGSVGElement>>): React.ReactNode => {
        const IconComponent = icon;

        return <IconComponent />;
    };

    const isPageAtTop = (): boolean => {
        return document.body.scrollTop === 0;
    };

    const bindSwipe = useDrag(
        ({ down, movement: [_, my] }) => {
            if (!down && my > 5 && isVisibleDetails && isPageAtTop()) {
                setIsVisibleDetails(false);
            }
        },
        { axis: 'y' },
    );

    const toggleVisibleDetails = () => {
        setIsVisibleDetails((prev) => {
            const nextState = !prev;

            if (!nextState) {
                const topBlock = document.querySelector(`.${styles.card}`);
                if (topBlock) {
                    topBlock.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
            }

            return nextState;
        });
    };
    
    const [isVerificationModalOpen, setIsVerificationModalOpen] =
        useState(false);
    const userData =
        useAuth((state) => state.getUser());

    const [profileViewModel, setProfileViewModel] = useState(
        new ProfileViewModel()
    )

    useEffect(() => {
        setProfileViewModel(
            new ProfileViewModel()
                .setData(profileData?.data ?? null)
        )
    }, [profileData])

    const { t } = useTranslation();

    return isLoading ? (
        <Spiner />
    ) : (
        <>
            <div className={clsx(styles.container, isVisibleDetails && styles.visibleDetails)}>
                <div className={styles.card}>
                    {
                        <GalleryImages
                            photos={profileData?.data?.photos ?? []}
                            isNeedRestrictGalleryWidget={false} />
                    }
                    <div className={clsx(styles.cardInfo, isVisibleDetails && styles.visibleDetails)}>
                        <div className={styles.nameAndVerificationStatusInfo}>
                            <Typography
                                typeColor="white"
                                fontWeight="medium"
                                variant="h3"
                                className={styles.cardInfoText}>
                                {getProfileNameAndAge(profileData?.data)}
                            </Typography>
                            {
                                profileData
                                    ?.data
                                    ?.verification_status
                                    === 'verified' &&
                                <VerifiedAccountIcon
                                    width={24}
                                    height={24}
                                    className={styles.statusIcon}
                                    onClick={() => setIsVerificationModalOpen(true)} />
                            }
                            {
                                profileViewModel.isPremium() &&
                                <div className={clsx(styles.premiumStatus, styles.statusIcon)}>
                                    <PremiumIcon width={24} height={24} />
                                    <Typography fontWeight="medium" className={styles.premiumStatusLabel}>Premium</Typography>
                                </div>
                            }
                        </div>
                        <Typography
                            typeColor="white"
                            fontWeight="normal"
                            variant="p"
                            className={styles.descriptionIntro}>
                            {profileData?.data?.about_me}
                        </Typography>
                    </div>

                    <div className={clsx(styles.arrowTop, isVisibleDetails && styles.visibleDetails)} onClick={(event) => toggleVisibleDetails()}>
                        <ArrowTopFillIcon />
                    </div>
                </div>

                <Container className={clsx(styles.userInfo, isVisibleDetails && styles.visibleDetails)} zIndex={isVisibleDetails ? 10 : undefined}>
                    <div className={styles.userInfo__wrapper} {...bindSwipe()}>
                        <div className={styles.userInfo__header}>
                            <Typography
                                typeColor="black"
                                fontWeight="medium"
                                variant="span"
                                className={clsx(styles.city)}>
                                {profileData?.data?.is_online ? (
                                    <>
                                        <OnlineStatus /> {t('online')}
                                    </>
                                ) : (
                                    <>
                                        <OfflineStatus /> {t('offline')}
                                    </>
                                )}
                            </Typography>

                            {profileData?.data?.regions ? (
                                profileData?.data?.regions.map((region) => (
                                    <Typography
                                        typeColor="black"
                                        fontWeight="medium"
                                        variant="span"
                                        className={clsx(styles.city)}>
                                        <LocationIcon /> {region?.name}
                                    </Typography>
                                ))
                            ) : (
                                <Typography>{t('empty_regions')}</Typography>
                            )}
                        </div>

                        <div className={styles.nameAndVerificationStatusInfo}>
                            <Typography
                                typeColor="black"
                                fontWeight="medium"
                                variant="h3"
                                className={styles.userInfo__title}>
                                {getProfileNameAndAge(profileData?.data)}
                            </Typography>
                            {
                                profileData
                                    ?.data
                                    ?.verification_status
                                    === 'verified' &&
                                <VerifiedAccountIcon
                                    width={24}
                                    height={24}
                                    className={styles.verificationStatusInfo}
                                    onClick={() => setIsVerificationModalOpen(true)} />
                            }
                            {
                                profileViewModel.isPremium() &&
                                <PremiumIcon width={24} height={24} />
                            }
                        </div>

                        {profileData?.data?.about_me && (
                            <>
                                <div className={styles.label}>
                                    <Typography
                                        variant="label"
                                        fontWeight="normal"
                                        fontFamily="SFProText">
                                        {t('about')}
                                    </Typography>
                                </div>

                                <Typography className={styles.text} variant="p">
                                    {profileData?.data?.about_me}
                                </Typography>
                            </>
                        )}

                        {currentPurpose && (
                            <Tag icon={getIconComponent(currentPurpose.icon)} className={clsx(styles.tagInfo, styles.isIcon)}>
                                <div>
                                    <Typography className={styles.tagInfo__title} variant="p">
                                        {t('purpose_label')}
                                    </Typography>
                                    <Typography
                                        className={styles.tagInfo__text}
                                        variant="p"
                                        fontWeight="bold">
                                        {t(currentPurpose.text)}
                                    </Typography>
                                </div>
                            </Tag>
                        )}

                        {profileData?.data?.travel_plans && (
                            <>
                                <div className={styles.label}>
                                    <Typography
                                        variant="label"
                                        fontWeight="normal"
                                        fontFamily="SFProText">
                                        {t('travel_plans_label')}
                                    </Typography>
                                </div>

                                <Typography className={styles.text} variant="p">
                                    {profileData?.data?.travel_plans}
                                </Typography>
                            </>
                        )}

                        {
                            profileData?.data &&
                            <AdditionalInfo profile={profileData.data} />
                        }
                    </div>
                </Container>
            </div>
            <BottomSheet
                overlayClassName={styles.bottomsheetOverlay}
                isOpen={isVerificationModalOpen}
                isDeskorTop={false}
                onClose={() => setIsVerificationModalOpen(false)}>
                <div className={styles.verificationModalContent}>
                    <div className={styles.verificationModalContentIcon}>
                        <Avatar
                            src={profileViewModel.mainPhotoUrl}
                            size={96} />
                        <VerifiedAccountIcon
                            className={styles.verificationModalContentIconVerification}
                            width={28}
                            height={28} />
                    </div>
                    <div className={styles.verificationModalContentHeader}>
                        <Typography
                            className={styles.verificationModalContentHeaderTitle}
                            variant={'h5'}
                            fontWeight={'bold'}>
                            {t('profile.verification_done')}
                        </Typography>
                        <Typography className={styles.verificationModalContentHeaderDesc}>
                            {t('profile.verification_status')}
                        </Typography>
                    </div>
                    <div className={styles.verificationModalContentFootnote}>
                        <Button
                            typeColor="orange"
                            className={styles.verificationModalContentFootnoteBtn}
                            onClick={() => setIsVerificationModalOpen(false)}>
                            {t('ok_tertiary')}
                        </Button>
                    </div>
                </div>
            </BottomSheet>
        </>
    );
};
